// ============================================================================
// PÁGINA DE DESTAQUES - VERSÃO COMPACTA E ALINHADA (SIDE-BY-SIDE)
// ============================================================================
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
    Box, Typography, CircularProgress, Container, Grid,
    Card, Chip, List, ListItem, ListItemIcon, ListItemText,
    Avatar, IconButton, Dialog, DialogContent, Paper, alpha, Divider, Stack, Tooltip
} from '@mui/material';
import {
    EmojiEvents as TrophyIcon, WorkspacePremium as MedalIcon,
    Visibility, Work as WorkIcon,
    Groups as GroupsIcon, Engineering as EngineeringIcon,
    Assessment, Speed, Psychology, Verified,
    Business as BusinessIcon
} from '@mui/icons-material';

// --- CARD DE MÉTRICA DE COMPETÊNCIAS (Barras Internas - Mantido) ---
const ComparativeMetricCard = ({ label, value, average, color, max = 5 }) => {
    const safeValue = Math.min(Math.max(Number(value) || 0, 0), max);
    const safeAverage = Math.min(Math.max(Number(average) || 0, 0), max);
    const valuePercent = (safeValue / max) * 100;
    const averagePercent = (safeAverage / max) * 100;

    return (
        <Paper elevation={0} sx={{ 
            p: 2.5, 
            mb: 2, 
            border: '1px solid #f1f5f9', 
            borderRadius: '12px', 
            bgcolor: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.95rem' }}>
                    {label}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.7rem', display: 'block', lineHeight: 1 }}>Média</Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>{safeAverage.toFixed(1)}</Typography>
                    </Box>
                    <Box sx={{ width: '1px', height: '24px', bgcolor: '#e2e8f0' }} />
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ color: color, fontSize: '0.7rem', display: 'block', lineHeight: 1 }}>Nota</Typography>
                        <Typography variant="body1" sx={{ color: color, fontWeight: 800 }}>{safeValue.toFixed(1)}</Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ position: 'relative', height: 8, width: '100%', bgcolor: '#f8fafc', borderRadius: 4, overflow: 'visible' }}>
                <Box sx={{ 
                    position: 'absolute', top: 0, left: 0, height: '100%', 
                    width: `${valuePercent}%`, 
                    bgcolor: color, 
                    borderRadius: 4,
                }} />
                
                <Tooltip title={`Média: ${safeAverage.toFixed(1)}`} arrow placement="top">
                    <Box sx={{ 
                        position: 'absolute', top: -2, bottom: -2, 
                        left: `${averagePercent}%`, 
                        width: '2px', bgcolor: '#334155', 
                        zIndex: 2, borderRadius: 1, 
                        boxShadow: '0 0 0 2px white' 
                    }} />
                </Tooltip>
            </Box>
        </Paper>
    );
};

// --- STAT CARD (VERSÃO COMPACTA - MENOR E ALINHADO) ---
const StatCard = ({ title, value, subtext, icon, color }) => (
    <Paper elevation={0} sx={{ 
        p: 2, // Padding Reduzido (era 3)
        borderRadius: '12px', 
        bgcolor: 'white',
        borderLeft: `4px solid ${color}`, // Borda mais fina (era 6px)
        boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)', 
        display: 'flex', flexDirection: 'column', justifyContent: 'center', 
        height: '100%',
        minHeight: '110px' // Altura fixa para garantir alinhamento perfeito
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box sx={{ p: 0.8, borderRadius: '6px', bgcolor: alpha(color, 0.1), color: color }}>
                {React.cloneElement(icon, { fontSize: 'small' })} {/* Ícone menor */}
            </Box>
            <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing={0.5} sx={{ fontSize: '0.7rem' }}>
                {title}
            </Typography>
        </Box>
        {/* Fonte do Valor Reduzida (h5 em vez de h4) */}
        <Typography variant="h5" fontWeight={800} color="text.primary" sx={{ mb: 0, lineHeight: 1.2 }}>
            {value}
        </Typography>
        {subtext && <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem', mt: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subtext}</Typography>}
    </Paper>
);

const TopPerformersPage = () => {
    const { token, user } = useAuth();
    const [loading, setLoading] = useState(true);
    
    // Dados
    const [rankingData, setRankingData] = useState([]); 
    const [topManagers, setTopManagers] = useState([]); 
    const [topOperators, setTopOperators] = useState([]); 
    const [myTeamRank, setMyTeamRank] = useState([]); 
    const [globalStats, setGlobalStats] = useState({ avgCompany: 0, totalEvaluated: 0, bestAreaName: '-', bestAreaAvg: 0 });
    const [areaGeneralAvgs, setAreaGeneralAvgs] = useState({});
    const [units, setUnits] = useState([]);
    const [selectedUnitId, setSelectedUnitId] = useState('');
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const isAdminOrEditor = user && ['superadmin', 'admin', 'editor'].includes(user.role);
    const isManagerOnly = useMemo(() => {
        if (isAdminOrEditor) return false;
        const searchStr = 'vaga gerencial';
        const check = (field) => field && field.toString().toLowerCase().includes(searchStr);
        return user?.isGerencial || check(user?.jobDescription) || check(user?.cargo);
    }, [user, isAdminOrEditor]);

    // Helpers
    const findAllSubordinateIds = useCallback((startNodeId, allNodes) => {
        const subordinates = new Set();
        const queue = [startNodeId.toString()];
        while (queue.length > 0) {
            const currentId = queue.shift();
            const children = allNodes.filter(n => n.parent_id?.toString() === currentId);
            children.forEach(child => {
                if (!subordinates.has(child.id.toString())) { subordinates.add(child.id.toString()); queue.push(child.id.toString()); }
            });
        }
        return subordinates;
    }, []);

    const getScoreColor = (score) => {
        if (score >= 4.5) return '#16a34a'; 
        if (score >= 4.0) return '#65a30d'; 
        if (score >= 3.0) return '#ca8a04'; 
        return '#dc2626'; 
    };

    const getRankIcon = (index) => {
        if (index === 0) return <TrophyIcon sx={{ color: '#fbbf24', fontSize: 28 }} />;
        if (index === 1) return <MedalIcon sx={{ color: '#94a3b8', fontSize: 24 }} />;
        if (index === 2) return <MedalIcon sx={{ color: '#b45309', fontSize: 24 }} />;
        return <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 700, width: 24, textAlign: 'center' }}>{index + 1}</Typography>;
    };

    // Handlers
    const handleOpenDetails = (employee) => { setSelectedEmployee(employee); setDetailsOpen(true); };
    const handleCloseDetails = () => { setDetailsOpen(false); setSelectedEmployee(null); };

    // Fetch Logic
    useEffect(() => {
        const fetchUnits = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/units/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
                const data = await response.json();
                setUnits(data || []);
                const userUnitId = user?.unitid || user?.unit_id;
                if (userUnitId) setSelectedUnitId(userUnitId.toString());
                else if (data && data.length > 0) setSelectedUnitId(data[0].id.toString());
            } catch (error) { console.error("Erro unidades:", error); }
        };
        if (token) fetchUnits();
    }, [token, user]);

    useEffect(() => {
        const calculateRanking = async () => {
            if (!selectedUnitId) return;
            setLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/organograma/unit/${selectedUnitId}`, { headers: { 'Authorization': `Bearer ${token}` } });
                const allNodes = await response.json();
                if (!allNodes) return;

                let filteredNodes = [];
                if (isAdminOrEditor) { filteredNodes = allNodes; } 
                else {
                    const myNode = allNodes.find(n => n.user_id?.toString() === user?.id?.toString());
                    if (myNode) { const subordinateIds = findAllSubordinateIds(myNode.id, allNodes); filteredNodes = allNodes.filter(n => subordinateIds.has(n.id.toString())); } 
                    else { const managerAreaId = user?.areaid || user?.area_id; filteredNodes = allNodes.filter(n => n.area_id?.toString() === managerAreaId?.toString() && n.user_id?.toString() !== user?.id?.toString()); }
                }

                const areaGroups = {};
                const allEmployeesProcessed = [];
                let totalCompanySum = 0; let totalCompanyCount = 0; let totalPotentialEvaluated = 0; let totalActuallyEvaluated = 0;
                const areaTotalSum = {}; const areaTotalCount = {};

                filteredNodes.forEach(node => {
                    const areaName = node.areaName || 'Sem Área';
                    if (!areaGroups[areaName]) {
                        areaGroups[areaName] = { areaName, totalCollaborators: 0, evaluatedCount: 0, employees: [], areaSum: 0 };
                        areaTotalSum[areaName] = 0; areaTotalCount[areaName] = 0;
                    }
                    if (node.user_id) {
                        areaGroups[areaName].totalCollaborators++; totalPotentialEvaluated++;
                        const hasFinishedCycle = node.evaluationHistory && Array.isArray(node.evaluationHistory) && node.evaluationHistory.length > 0;
                        if (hasFinishedCycle) {
                            areaGroups[areaName].evaluatedCount++; totalActuallyEvaluated++;
                            const latestFinishedData = [...node.evaluationHistory].sort((a, b) => new Date(b.date) - new Date(a.date))[0].evaluations || {};
                            const techList = new Set([...(node.competenciasTecnicas || []).map(c => c.nome || c), ...(node.outrasCompetencias || []).map(c => c.nome || c)]);
                            let sumCorporate = 0, countCorporate = 0, sumManagerial = 0, countManagerial = 0, sumTechnical = 0, countTechnical = 0, sumGeneral = 0, countGeneral = 0;
                            const correctedEvaluations = {};
                            Object.entries(latestFinishedData).forEach(([compKey, compData]) => {
                                const rating = compData.rating || 0;
                                const originalType = (compData.competencyType || 'OUTROS').toUpperCase();
                                let realType = (techList.has(compKey) || techList.has(compData.competencyName)) ? 'TÉCNICA' : (originalType.includes('GERENCIAL') ? 'GERENCIAL' : 'COMPORTAMENTAL');
                                correctedEvaluations[compKey] = { ...compData, competencyType: realType, rating: rating };
                                if (rating > 0) {
                                    sumGeneral += rating; countGeneral++;
                                    if (realType === 'GERENCIAL') { sumManagerial += rating; countManagerial++; } else if (realType === 'TÉCNICA') { sumTechnical += rating; countTechnical++; } else { sumCorporate += rating; countCorporate++; }
                                }
                            });
                            const generalAvg = countGeneral > 0 ? sumGeneral / countGeneral : 0;
                            if (generalAvg > 0) { totalCompanySum += generalAvg; totalCompanyCount++; areaTotalSum[areaName] += generalAvg; areaTotalCount[areaName]++; areaGroups[areaName].areaSum += generalAvg; }
                            const employeeObj = {
                                id: node.id, name: node.nome, role: node.cargo, avatar: node.profile_picture_url, areaName: areaName, fullEvaluationData: correctedEvaluations, 
                                averages: { general: generalAvg, corporate: countCorporate > 0 ? sumCorporate / countCorporate : 0, managerial: countManagerial > 0 ? sumManagerial / countManagerial : 0, technical: countTechnical > 0 ? sumTechnical / countTechnical : 0 },
                                isManager: node.isGerencial || countManagerial > 0
                            };
                            areaGroups[areaName].employees.push(employeeObj); allEmployeesProcessed.push(employeeObj);
                        }
                    }
                });

                const finalCompanyAvg = totalCompanyCount > 0 ? totalCompanySum / totalCompanyCount : 0;
                const finalAreaStats = {};
                let bestArea = { name: '-', avg: 0 };
                Object.keys(areaGroups).forEach(area => {
                    const avg = areaTotalCount[area] > 0 ? areaTotalSum[area] / areaTotalCount[area] : 0;
                    finalAreaStats[area] = avg; areaGroups[area].averageScore = avg;
                    if (avg > bestArea.avg && areaGroups[area].evaluatedCount > 0) { bestArea = { name: area, avg: avg }; }
                });

                setGlobalStats({ avgCompany: finalCompanyAvg, totalEvaluated: totalActuallyEvaluated, bestAreaName: bestArea.name, bestAreaAvg: bestArea.avg });
                setAreaGeneralAvgs(finalAreaStats);
                const sortedAll = allEmployeesProcessed.sort((a, b) => b.averages.general - a.averages.general);
                if (isManagerOnly) { setMyTeamRank(sortedAll.slice(0, 10)); } else { setTopManagers(allEmployeesProcessed.filter(e => e.isManager).sort((a, b) => b.averages.general - a.averages.general).slice(0, 5)); setTopOperators(allEmployeesProcessed.filter(e => !e.isManager).sort((a, b) => b.averages.general - a.averages.general).slice(0, 5)); }
                setRankingData(Object.values(areaGroups).sort((a, b) => b.averageScore - a.averageScore)); 
            } catch (e) { console.error(e); } finally { setLoading(false); }
        };
        calculateRanking();
    }, [selectedUnitId, token, user, isAdminOrEditor, findAllSubordinateIds, isManagerOnly]);

    // --- LISTA COM ESPAÇAMENTO AJUSTADO (PRESERVADA) ---
    const RankingList = ({ employees }) => (
        <List sx={{ p: 0 }}>
            {employees.map((emp, index) => (
                <ListItem 
                    key={emp.id} divider
                    secondaryAction={
                        <IconButton onClick={() => handleOpenDetails(emp)} size="small" sx={{ bgcolor: '#f8fafc', '&:hover': { bgcolor: '#f1f5f9' } }}>
                            <Visibility fontSize="small" sx={{ color: '#94a3b8' }} />
                        </IconButton>
                    }
                    sx={{ px: 3, py: 2 }}
                >
                    <ListItemIcon sx={{ minWidth: 40 }}>{getRankIcon(index)}</ListItemIcon>
                    
                    <ListItemText 
                        primary={<Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>{emp.name}</Typography>}
                        secondary={<Typography variant="caption" color="text.secondary">{emp.role}</Typography>}
                        sx={{ flexGrow: 1, mr: 2 }} 
                    />

                    <Box sx={{ 
                        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                        minWidth: '60px', ml: 4, mr: 4 
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: getScoreColor(emp.averages.general) }}>
                            {emp.averages.general.toFixed(2)}
                        </Typography>
                    </Box>
                </ListItem>
            ))}
        </List>
    );

    const CompetencySection = ({ title, comps, color, icon, areaAverage }) => {
        if (!comps || comps.length === 0) return null;
        return (
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, pl: 1 }}>
                    <Box sx={{ color: color }}>{icon}</Box>
                    <Typography variant="subtitle2" sx={{ color: '#334155', fontWeight: 800, letterSpacing: '0.5px' }}>{title}</Typography>
                </Box>
                {comps.map((comp, i) => (
                    <ComparativeMetricCard key={i} label={comp.name} value={comp.score} average={areaAverage} color={color} />
                ))}
            </Box>
        );
    };

    const renderModalContent = () => {
        if (!selectedEmployee) return null;
        const allComps = Object.entries(selectedEmployee.fullEvaluationData).map(([name, data]) => ({ name, score: Number(data.rating), type: data.competencyType })).filter(c => c.score > 0);
        const techComps = allComps.filter(c => c.type === 'TÉCNICA').sort((a, b) => b.score - a.score);
        const behavioralComps = allComps.filter(c => c.type !== 'TÉCNICA' && c.type !== 'GERENCIAL').sort((a, b) => b.score - a.score);
        const managerialComps = allComps.filter(c => c.type === 'GERENCIAL').sort((a, b) => b.score - a.score);
        const areaAvg = areaGeneralAvgs[selectedEmployee.areaName] || 0;

        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4, pb: 3, borderBottom: '1px solid #f1f5f9' }}>
                    <Avatar src={selectedEmployee.avatar ? `${process.env.REACT_APP_API_URL}/uploads/${selectedEmployee.avatar.replace(/\\/g, '/')}` : ''} sx={{ width: 72, height: 72, border: '3px solid white', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }} />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>{selectedEmployee.name}</Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>{selectedEmployee.role}</Typography>
                    </Box>
                    <Paper elevation={0} sx={{ px: 3, py: 1.5, bgcolor: getScoreColor(selectedEmployee.averages.general), color: 'white', borderRadius: '12px', textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>{selectedEmployee.averages.general.toFixed(2)}</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', opacity: 0.9 }}>Média Geral</Typography>
                    </Paper>
                </Box>

                <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: '16px', border: '1px solid #f1f5f9', position: 'sticky', top: 0 }}>
                            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 700, color: '#334155' }}>RESUMO DE NOTAS</Typography>
                            <Stack spacing={2}>
                                {['Técnicas', 'Comportamentais', 'Gerenciais'].map((label, idx) => {
                                    const val = [selectedEmployee.averages.technical, selectedEmployee.averages.corporate, selectedEmployee.averages.managerial][idx];
                                    const col = ['#0ea5e9', '#8b5cf6', '#f59e0b'][idx];
                                    if (val === 0) return null;
                                    return (
                                        <Box key={label}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ color: '#64748b' }}>{label}</Typography>
                                                <Typography variant="caption" fontWeight={700} sx={{ color: col }}>{val.toFixed(2)}</Typography>
                                            </Box>
                                            <Box sx={{ height: 6, bgcolor: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                                                <Box sx={{ width: `${(val/5)*100}%`, height: '100%', bgcolor: col }} />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Stack>
                            <Divider sx={{ my: 3 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" color="text.secondary">Média da Área</Typography>
                                    <Typography variant="caption" fontWeight={700}>{areaAvg.toFixed(2)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" color="text.secondary">Média da Empresa</Typography>
                                    <Typography variant="caption" fontWeight={700} color="#334155">{globalStats.avgCompany.toFixed(2)}</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <CompetencySection title="TÉCNICAS" comps={techComps} color="#0284c7" icon={<Verified fontSize="small" />} areaAverage={areaAvg} />
                        <CompetencySection title="COMPORTAMENTAIS" comps={behavioralComps} color="#7c3aed" icon={<Psychology fontSize="small" />} areaAverage={areaAvg} />
                        <CompetencySection title="GERENCIAIS" comps={managerialComps} color="#d97706" icon={<Assessment fontSize="small" />} areaAverage={areaAvg} />
                        {allComps.length === 0 && <Box sx={{ py: 4, textAlign: 'center', color: '#94a3b8' }}>Sem dados.</Box>}
                    </Grid>
                </Grid>
            </Box>
        );
    };

    return (
        <Container maxWidth="lg" sx={{ pb: 8, pt: 4 }}>
            <Box sx={{ mb: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <TrophyIcon sx={{ color: '#f59e0b' }} /> Painel de Performance
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                        {globalStats.totalEvaluated} avaliações analisadas.
                    </Typography>
                </Box>
                {isAdminOrEditor && (
                    <select value={selectedUnitId} onChange={(e) => setSelectedUnitId(e.target.value)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', outline: 'none', backgroundColor: 'white', color: '#334155' }}>
                        {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                )}
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
            ) : (
                <>
                    {/* KPIs - 4 cards pequenos em uma linha (Grid Spacing 2) */}
                    <Grid container spacing={2} sx={{ mb: 5 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Média Global" value={globalStats.avgCompany.toFixed(2)} icon={<Speed />} color="#3b82f6" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Avaliações" value={globalStats.totalEvaluated} icon={<Assessment />} color="#10b981" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Melhor Área" value={globalStats.bestAreaAvg.toFixed(2)} subtext={globalStats.bestAreaName} icon={<TrophyIcon />} color="#f59e0b" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Unidade" value={units.find(u => u.id.toString() === selectedUnitId)?.name || '...'} icon={<BusinessIcon />} color="#6366f1" />
                        </Grid>
                    </Grid>

                    {isManagerOnly ? (
                        <Card elevation={0} sx={{ mb: 5, borderRadius: '16px', border: '1px solid #f1f5f9', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                            <Box sx={{ p: 2.5, borderBottom: '1px solid #f1f5f9' }}><Typography variant="h6" fontWeight={800} color="#1e293b">Meu Time</Typography></Box>
                            <RankingList employees={myTeamRank} />
                        </Card>
                    ) : (
                        <Grid container spacing={3} sx={{ mb: 5 }}>
                            <Grid item xs={12} md={6}>
                                <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                                    <Box sx={{ p: 2.5, bgcolor: '#f0f9ff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <EngineeringIcon sx={{ color: '#0ea5e9' }} /> <Typography variant="h6" fontWeight={800} color="#0c4a6e">Top Gestores</Typography>
                                    </Box>
                                    <RankingList employees={topManagers} />
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                                    <Box sx={{ p: 2.5, bgcolor: '#fdf4ff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <WorkIcon sx={{ color: '#d946ef' }} /> <Typography variant="h6" fontWeight={800} color="#701a75">Top Operacional</Typography>
                                    </Box>
                                    <RankingList employees={topOperators} />
                                </Card>
                            </Grid>
                        </Grid>
                    )}

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#334155', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <GroupsIcon color="action" /> Rankings por Setor
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {rankingData.map((group) => (
                            <Grid item xs={12} md={6} lg={4} key={group.areaName}>
                                <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                                    <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }}>{group.areaName}</Typography>
                                        <Chip label={group.averageScore.toFixed(2)} size="small" sx={{ fontWeight: 700, bgcolor: 'white', border: '1px solid #e2e8f0' }} />
                                    </Box>
                                    <Box sx={{ flexGrow: 1 }}>
                                        {group.employees.length > 0 ? <RankingList employees={group.employees.slice(0, 3)} /> : <Box sx={{ p: 4, textAlign: 'center', color: '#cbd5e1' }}>Aguardando dados.</Box>}
                                    </Box>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            <Dialog open={detailsOpen} onClose={handleCloseDetails} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
                <DialogContent sx={{ p: 4 }}>{renderModalContent()}</DialogContent>
            </Dialog>
        </Container>
    );
};

export default TopPerformersPage;